<script setup lang="ts">
import NavBar from "@/components/navBar/NavBar.vue";
import {onBeforeMount, onUnmounted, ref, watch} from "vue";
import LinkUtils from "@/utils/common/LinkUtils";
import PlaceTokens from "@/utils/constants/PlaceTokens";
import Translator from "@/utils/common/Translator";
import SideBar from "@/components/SideBar.vue";
import SideBarUtils from "@/utils/common/SideBarUtils";
import {onClickOutside} from "@vueuse/core";
import {type RouteLocationNormalizedLoaded, useRoute, useRouter} from "vue-router";
import RouteNames, {CustomPlaceTokens} from "@/router/RouteNames.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {storeToRefs} from "pinia";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import WelcomePlace from "@/utils/presenters/WelcomePlace.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import EditViewUtils from "@/utils/editView/EditViewUtils.ts";

const {isSideBarOpen, sidebarFixed, darkNavBarAndSideBar} = storeToRefs(useNamaFlagsStore());

GUIUtilities.updateWindowTitle(Translator.translateId('homePage'));
const route = useRoute();
const router = useRouter();

function showResetPasswordByTokenDialog(resetToken: string, resetUserId: string) {
  //TODO:: Create Route For ResetPasswordByToken
}

onBeforeMount(() => {
  let resetUser = LinkUtils.fetchQueryToken(PlaceTokens.PRM_RESET_USER);
  let resetToken = LinkUtils.fetchQueryToken(PlaceTokens.PRM_RESET_TOKEN);
  if (resetToken && resetUser) {
    showResetPasswordByTokenDialog(resetToken, resetUser);
  } else {
    // tryLoginFromSession();
  }
})

const leftDrawerOpen = ref(SideBarUtils.isSidebarFixed());
watch(() => sidebarFixed.value, (fixed) => {
  if (fixed)
    leftDrawerOpen.value = true;
})
watch(() => leftDrawerOpen.value, (v) => isSideBarOpen.value = v);
watch(() => isSideBarOpen.value, (v) => leftDrawerOpen.value = v);
const sidebar = ref(null);

onClickOutside(sidebar, function (e) {
  if (SideBarUtils.isSidebarFixed())
    return;
  if (document.getElementById("sideBarBtn")?.contains((e.target) as Node))
    return;
  leftDrawerOpen.value = false;
})

function toggleSideBar() {
  leftDrawerOpen.value = !leftDrawerOpen.value
}

function currentRouteKey(target: RouteLocationNormalizedLoaded = route): string | undefined {
  if (!target.name || target.name === RouteNames.WELCOME_ROUTE) {
    let p = new WelcomePlace(target.query as Record<string, string>);
    return p.dashBoardId + "_" + p.flowId + "_" + p.importObjects + "_" + p.flowTargetId;
  }
  if (target.name === RouteNames.LIST_VIEW_ROUTE || target.name === RouteNames.REPORT_LIST_ROUTE) {
    let key: string = target.name;
    if (target.query[PlaceTokens.PRM_ENTITY_TYPE])
      key += '_' + target.query[PlaceTokens.PRM_ENTITY_TYPE];
    if (target.query[PlaceTokens.PRM_MENU_HASH])
      key += '_' + target.query[PlaceTokens.PRM_MENU_HASH];
    if (target.query[PlaceTokens.PRM_LIST_VIEW_NAME])
      key += '_' + target.query[PlaceTokens.PRM_LIST_VIEW_NAME];
    if (target.query[CustomPlaceTokens.FORCE_REFRESH_KEY])
      key += '_' + target.query[CustomPlaceTokens.FORCE_REFRESH_KEY];
    return key;
  } else if (target.name === RouteNames.EDIT_VIEW_ROUTE) {
    let key = RouteNames.EDIT_VIEW_ROUTE;
    if (target.query[PlaceTokens.PRM_ENTITY_TYPE])
      key += '_' + target.query[PlaceTokens.PRM_ENTITY_TYPE];
    if (target.query[PlaceTokens.PRM_EDIT_VIEW_NAME])
      key += '_' + target.query[PlaceTokens.PRM_EDIT_VIEW_NAME];
    if (target.query[CustomPlaceTokens.FORCE_REFRESH_KEY])
      key += '_' + target.query[CustomPlaceTokens.FORCE_REFRESH_KEY];
    return key;
  } else if (target.name === RouteNames.LAUNCH_REPORT_ROUTE) {
    let key = RouteNames.LAUNCH_REPORT_ROUTE;
    if (target.query[PlaceTokens.PRM_ID])
      key += '_' + target.query[PlaceTokens.PRM_ID];
    if (target.query[PlaceTokens.PRM_QUESTIONS])
      key += '_' + target.query[PlaceTokens.PRM_QUESTIONS];
    return key;
  } else if (target.name === RouteNames.REPORT_RESULT_ROUTE) {
    let key = RouteNames.REPORT_RESULT_ROUTE;
    if (target.query[PlaceTokens.PRM_ID])
      key += '_' + target.query[PlaceTokens.PRM_ID];
    if (target.query[PlaceTokens.PRM_QUESTIONS])
      key += '_' + target.query[PlaceTokens.PRM_QUESTIONS];
    return key;
  }
  return undefined;
}

// A new edit screen starts without the previous screen's toasts. Clearing here, before the screen loads
// anything, keeps the errors of its own loading (a record that does not exist) on screen, and the
// critical errors pushed at login too, since logging in is not a navigation.
const removeClearErrorsHook = router.afterEach((to, from, failure) => {
  if (!failure && to.name === RouteNames.EDIT_VIEW_ROUTE && currentRouteKey(to) !== currentRouteKey(from))
    EditViewUtils.clearErrors();
});
onUnmounted(removeClearErrorsHook);
</script>

<template>
  <div class="tw-w-full tw-h-full">
    <q-layout view="lHh LpR lFf" style="min-height: 100vh; max-height: 100vh;">
      <q-header :data-drawer-openned="leftDrawerOpen" style="height:var(--navbar-height)" :class="{'tw-dark nama-dark-bars': darkNavBarAndSideBar}">
        <q-toolbar style="padding: 0;height:var(--navbar-height); min-height: var(--navbar-height);">
          <NavBar @toggleSideBar="toggleSideBar"/>
        </q-toolbar>
      </q-header>
      <q-drawer v-model="leftDrawerOpen" bordered :dark="darkNavBarAndSideBar || undefined" :class="{'tw-dark nama-dark-bars': darkNavBarAndSideBar}">
        <SideBar ref="sidebar"/>
      </q-drawer>
      <q-page-container :data-drawer-openned="leftDrawerOpen" class="tw-h-[100vh] tw-relative tw-overflow-hidden">
        <RouterView :key="currentRouteKey()"/>
        <img v-if="ClientCache.getBgImageUrl()" :src="ClientCache.getBgImageUrl()" alt="bg-img"
             class="tw-absolute tw-top-14 -tw-translate-x-1/2 tw-z-[-1] tw- tw-h-[95%] tw-object-cover"
             :class="[Translator.fetchDir() === Translator.RTL ? 'tw-right-1/2':'tw-left-1/2',route.name==RouteNames.WELCOME_ROUTE?'tw-opacity-25':'tw-opacity-15']"/>
      </q-page-container>
    </q-layout>
  </div>
</template>

<style>
[dir=rtl] aside.q-drawer.q-drawer--left:where(.tw-dark,.tw-dark *) {
  border-left-color: #6C757D
}

[dir=rtl] aside.q-drawer.q-drawer--left:where(.tw-dark,.tw-dark *) {
  border-right-color: #6C757D
}
</style>
